/** App imports */
import {
  LogsApiSystemColumns,
  ClassificationModelEstimators,
  RegressionModelEstimators,
} from '../../../TypeScript/enums';
import { EstimatorOption } from '../../../TypeScript/interfaces';

export const CROSS_VALIDATION_OPTIONS = {
  DRILL_HOLE: 'drill_hole',
  K_FOLD: 'k_fold',
  NONE: 'none',
} as const;

export const COOL_DOWN_TIMER = 5000; // 5 seconds

export const modelDetailsTabs = {
  metrics: 'metrics',
  addToLibrary: 'addToLibrary',
  optimization: 'optimization',
};

export const modelIdParamKey = 'modelId';

export const modelDetailsParamKey = 'tab';

export const objectTypeModel = 'model';

export const modelsHomeFolder = 'models';

export const trainingSplitDefault = '80';

export const estimatorNumberDefault = 100;

export const estimatorsMin = 1;

export const estimatorsMax = 1000;

export const maxDepthDefault = '';

export const maxDepthMin = 1;

export const maxDepthMax = 100;

export const crossValidationFoldsDefault = '10';

export const crossValidationFoldsMin = 2;

export const trainingSplitPercentDominator = 100;

export const trainingSplitMin = 5;

export const trainingSplitMax = 95;

export const excludeFeatureColumnsSet = new Set<string>([
  LogsApiSystemColumns._id,
  LogsApiSystemColumns.createdAt,
  LogsApiSystemColumns.createdBy,
  LogsApiSystemColumns.drillholeId,
  LogsApiSystemColumns.fromDepth,
  LogsApiSystemColumns.logId,
  LogsApiSystemColumns.toDepth,
  LogsApiSystemColumns.updatedAt,
  LogsApiSystemColumns.updatedBy,
  'index', // Added by Pandas DataFrame during preprocessing in micro service
]);

export const modelTaskTypeMap: Record<string, { label: string }> = {
  classification: { label: 'TRAIN_MODEL_TASK_CLASSIFICATION' },
  regression: { label: 'TRAIN_MODEL_TASK_REGRESSION' },
};

export const defaultClassificationEstimator =
  ClassificationModelEstimators.randomForest;
export const defaultRegressionEstimator = RegressionModelEstimators.lightGBM;

/**
 * Estimators that support the `n_estimators` hyperparameter
 */
export const ESTIMATORS_SUPPORTING_N_ESTIMATORS = new Set<string>([
  ClassificationModelEstimators.randomForest,
  ClassificationModelEstimators.extraTrees,
  ClassificationModelEstimators.gradientBoosting,
  ClassificationModelEstimators.lightGBM,
  ClassificationModelEstimators.adaBoost,
  RegressionModelEstimators.randomForest,
  RegressionModelEstimators.extraTrees,
  RegressionModelEstimators.gradientBoosting,
  RegressionModelEstimators.lightGBM,
  RegressionModelEstimators.adaBoost,
]);

/**
 * Estimators that support the `max_depth` hyperparameter
 */
export const ESTIMATORS_SUPPORTING_MAX_DEPTH = new Set<string>([
  ClassificationModelEstimators.randomForest,
  ClassificationModelEstimators.extraTrees,
  ClassificationModelEstimators.gradientBoosting,
  ClassificationModelEstimators.lightGBM,
  ClassificationModelEstimators.decisionTree,
  RegressionModelEstimators.randomForest,
  RegressionModelEstimators.extraTrees,
  RegressionModelEstimators.gradientBoosting,
  RegressionModelEstimators.lightGBM,
  RegressionModelEstimators.decisionTree,
]);

/**
 * Estimators that do not support Permutation Feature Importance plot generation
 */
export const SKIP_PLOT_PFI = new Set<string>([
  ClassificationModelEstimators.gaussianProcess,
]);

/**
 * Estimators that do not support Precision-Recall Curve plot generation
 */
export const SKIP_PLOT_PR = new Set<string>([
  ClassificationModelEstimators.svm,
]);

/**
 * Estimators that do not support ROC Curve plot generation
 */
export const SKIP_PLOT_ROC = new Set<string>([
  ClassificationModelEstimators.svm,
]);

/**
 * Estimators that do not support SHAP Summary plot generation
 */
export const SKIP_PLOT_SHAP = new Set<string>([
  ClassificationModelEstimators.svm,
  ClassificationModelEstimators.rbfSvm,
  ClassificationModelEstimators.gaussianProcess,
  ClassificationModelEstimators.gradientBoosting,
]);

export const classificationModelEstimatorMap: Record<string, EstimatorOption> =
  {
    [ClassificationModelEstimators.randomForest]: {
      label: 'ESTIMATOR_RANDOM_FOREST',
      value: ClassificationModelEstimators.randomForest,
      isRecommended: true,
      description: 'ESTIMATOR_RANDOM_FOREST_INFO',
    },
    [ClassificationModelEstimators.lightGBM]: {
      label: 'ESTIMATOR_LIGHT_GBM',
      value: ClassificationModelEstimators.lightGBM,
      isRecommended: true,
      description: 'ESTIMATOR_LIGHT_GBM_INFO',
    },
    [ClassificationModelEstimators.gradientBoosting]: {
      label: 'ESTIMATOR_GRADIENT_BOOSTING',
      value: ClassificationModelEstimators.gradientBoosting,
      isRecommended: true,
      description: 'ESTIMATOR_GRADIENT_BOOSTING_INFO',
    },
    [ClassificationModelEstimators.adaBoost]: {
      label: 'ESTIMATOR_ADA_BOOST',
      value: ClassificationModelEstimators.adaBoost,
      isRecommended: false,
      description: 'ESTIMATOR_ADA_BOOST_INFO',
    },
    [ClassificationModelEstimators.decisionTree]: {
      label: 'ESTIMATOR_DECISION_TREE',
      value: ClassificationModelEstimators.decisionTree,
      isRecommended: false,
      description: 'ESTIMATOR_DECISION_TREE_INFO',
    },
    [ClassificationModelEstimators.extraTrees]: {
      label: 'ESTIMATOR_EXTRA_TREES',
      value: ClassificationModelEstimators.extraTrees,
      isRecommended: true,
      description: 'ESTIMATOR_EXTRA_TREES_INFO',
    },
    [ClassificationModelEstimators.gaussianProcess]: {
      label: 'ESTIMATOR_GAUSSIAN_PROCESS',
      value: ClassificationModelEstimators.gaussianProcess,
      isRecommended: false,
      description: 'ESTIMATOR_GAUSSIAN_PROCESS_INFO',
    },
    [ClassificationModelEstimators.kNeighbors]: {
      label: 'ESTIMATOR_K_NEIGHBORS',
      value: ClassificationModelEstimators.kNeighbors,
      isRecommended: false,
      description: 'ESTIMATOR_K_NEIGHBORS_INFO',
    },
    [ClassificationModelEstimators.lda]: {
      label: 'ESTIMATOR_LDA',
      value: ClassificationModelEstimators.lda,
      isRecommended: false,
      description: 'ESTIMATOR_LDA_INFO',
    },
    [ClassificationModelEstimators.logisticRegression]: {
      label: 'ESTIMATOR_LOGISTIC_REGRESSION',
      value: ClassificationModelEstimators.logisticRegression,
      isRecommended: false,
      description: 'ESTIMATOR_LOGISTIC_REGRESSION_INFO',
    },
    [ClassificationModelEstimators.mlp]: {
      label: 'ESTIMATOR_MLP',
      value: ClassificationModelEstimators.mlp,
      isRecommended: false,
      description: 'ESTIMATOR_MLP_INFO',
    },
    [ClassificationModelEstimators.naiveBayes]: {
      label: 'ESTIMATOR_NAIVE_BAYES',
      value: ClassificationModelEstimators.naiveBayes,
      isRecommended: false,
      description: 'ESTIMATOR_NAIVE_BAYES_INFO',
    },
    [ClassificationModelEstimators.qda]: {
      label: 'ESTIMATOR_QDA',
      value: ClassificationModelEstimators.qda,
      isRecommended: false,
      description: 'ESTIMATOR_QDA_INFO',
    },
    [ClassificationModelEstimators.rbfSvm]: {
      label: 'ESTIMATOR_RBF_SVM',
      value: ClassificationModelEstimators.rbfSvm,
      isRecommended: false,
      description: 'ESTIMATOR_RBF_SVM_INFO',
    },
    [ClassificationModelEstimators.svm]: {
      label: 'ESTIMATOR_SVM',
      value: ClassificationModelEstimators.svm,
      isRecommended: false,
      description: 'ESTIMATOR_SVM_INFO',
    },
    [ClassificationModelEstimators.ridge]: {
      label: 'ESTIMATOR_RIDGE',
      value: ClassificationModelEstimators.ridge,
      isRecommended: false,
      description: 'ESTIMATOR_RIDGE_INFO',
      isDisabled: true,
    },
    [ClassificationModelEstimators.xgBoost]: {
      label: 'ESTIMATOR_X_G_BOOST',
      value: ClassificationModelEstimators.xgBoost,
      isRecommended: false,
      description: 'ESTIMATOR_X_G_BOOST_INFO',
      isDisabled: true,
    },
    [ClassificationModelEstimators.catBoost]: {
      label: 'ESTIMATOR_CAT_BOOST',
      value: ClassificationModelEstimators.catBoost,
      isRecommended: false,
      description: 'ESTIMATOR_CAT_BOOST_INFO',
      isDisabled: true,
    },
  };

export const regressionModelEstimatorMap: Record<string, EstimatorOption> = {
  [RegressionModelEstimators.linearRegression]: {
    label: 'ESTIMATOR_LINEAR_REGRESSION',
    value: RegressionModelEstimators.linearRegression,
    isRecommended: false,
    description: 'ESTIMATOR_LINEAR_REGRESSION_INFO',
  },
  [RegressionModelEstimators.lightGBM]: {
    label: 'ESTIMATOR_LIGHT_GBM',
    value: RegressionModelEstimators.lightGBM,
    isRecommended: true,
    description: 'ESTIMATOR_LIGHT_GBM_INFO',
  },
  [RegressionModelEstimators.adaBoost]: {
    label: 'ESTIMATOR_ADA_BOOST',
    value: RegressionModelEstimators.adaBoost,
    isRecommended: false,
    description: 'ESTIMATOR_ADA_BOOST_INFO',
  },
  [RegressionModelEstimators.ard]: {
    label: 'ESTIMATOR_ARD',
    value: RegressionModelEstimators.ard,
    isRecommended: false,
    description: 'ESTIMATOR_ARD_INFO',
  },
  [RegressionModelEstimators.bayesianRidge]: {
    label: 'ESTIMATOR_BAYESIAN_RIDGE',
    value: RegressionModelEstimators.bayesianRidge,
    isRecommended: false,
    description: 'ESTIMATOR_BAYESIAN_RIDGE_INFO',
  },
  [RegressionModelEstimators.decisionTree]: {
    label: 'ESTIMATOR_DECISION_TREE',
    value: RegressionModelEstimators.decisionTree,
    isRecommended: false,
    description: 'ESTIMATOR_DECISION_TREE_INFO',
  },
  [RegressionModelEstimators.elasticNet]: {
    label: 'ESTIMATOR_ELASTIC_NET',
    value: RegressionModelEstimators.elasticNet,
    isRecommended: false,
    description: 'ESTIMATOR_ELASTIC_NET_INFO',
  },
  [RegressionModelEstimators.extraTrees]: {
    label: 'ESTIMATOR_EXTRA_TREES',
    value: RegressionModelEstimators.extraTrees,
    isRecommended: false,
    description: 'ESTIMATOR_EXTRA_TREES_INFO',
  },
  [RegressionModelEstimators.gradientBoosting]: {
    label: 'ESTIMATOR_GRADIENT_BOOSTING_REGRESSION',
    value: RegressionModelEstimators.gradientBoosting,
    isRecommended: true,
    description: 'ESTIMATOR_GRADIENT_BOOSTING_REGRESSION_INFO',
  },
  [RegressionModelEstimators.huber]: {
    label: 'ESTIMATOR_HUBER',
    value: RegressionModelEstimators.huber,
    isRecommended: false,
    description: 'ESTIMATOR_HUBER_INFO',
  },
  [RegressionModelEstimators.kNeighbors]: {
    label: 'ESTIMATOR_K_NEIGHBORS',
    value: RegressionModelEstimators.kNeighbors,
    isRecommended: false,
    description: 'ESTIMATOR_K_NEIGHBORS_INFO',
  },
  [RegressionModelEstimators.kernelRidge]: {
    label: 'ESTIMATOR_KERNEL_RIDGE',
    value: RegressionModelEstimators.kernelRidge,
    isRecommended: false,
    description: 'ESTIMATOR_KERNEL_RIDGE_INFO',
  },
  [RegressionModelEstimators.lasso]: {
    label: 'ESTIMATOR_LASSO',
    value: RegressionModelEstimators.lasso,
    isRecommended: false,
    description: 'ESTIMATOR_LASSO_INFO',
  },
  [RegressionModelEstimators.lassoLeastAngle]: {
    label: 'ESTIMATOR_LASSO_LEAST_ANGLE',
    value: RegressionModelEstimators.lassoLeastAngle,
    isRecommended: false,
    description: 'ESTIMATOR_LASSO_LEAST_ANGLE_INFO',
  },
  [RegressionModelEstimators.leastAngleRegression]: {
    label: 'ESTIMATOR_LEAST_ANGLE_REGRESSION',
    value: RegressionModelEstimators.leastAngleRegression,
    isRecommended: false,
    description: 'ESTIMATOR_LEAST_ANGLE_REGRESSION_INFO',
  },
  [RegressionModelEstimators.mlp]: {
    label: 'ESTIMATOR_MLP',
    value: RegressionModelEstimators.mlp,
    isRecommended: false,
    description: 'ESTIMATOR_MLP_INFO',
  },
  [RegressionModelEstimators.orthogonalMatchingPursuit]: {
    label: 'ESTIMATOR_ORTHOGONAL_MATCHING_PURSUIT',
    value: RegressionModelEstimators.orthogonalMatchingPursuit,
    isRecommended: false,
    description: 'ESTIMATOR_ORTHOGONAL_MATCHING_PURSUIT_INFO',
  },
  [RegressionModelEstimators.passiveAggressive]: {
    label: 'ESTIMATOR_PASSIVE_AGGRESSIVE',
    value: RegressionModelEstimators.passiveAggressive,
    isRecommended: false,
    description: 'ESTIMATOR_PASSIVE_AGGRESSIVE_INFO',
  },
  [RegressionModelEstimators.randomForest]: {
    label: 'ESTIMATOR_RANDOM_FOREST',
    value: RegressionModelEstimators.randomForest,
    isRecommended: true,
    description: 'ESTIMATOR_RANDOM_FOREST_INFO',
  },
  [RegressionModelEstimators.ransac]: {
    label: 'ESTIMATOR_RANSAC',
    value: RegressionModelEstimators.ransac,
    isRecommended: false,
    description: 'ESTIMATOR_RANSAC_INFO',
  },
  [RegressionModelEstimators.ridge]: {
    label: 'ESTIMATOR_RIDGE',
    value: RegressionModelEstimators.ridge,
    isRecommended: false,
    description: 'ESTIMATOR_RIDGE_INFO',
  },
  [RegressionModelEstimators.supportVectorRegression]: {
    label: 'ESTIMATOR_SUPPORT_VECTOR_REGRESSION',
    value: RegressionModelEstimators.supportVectorRegression,
    isRecommended: false,
    description: 'ESTIMATOR_SUPPORT_VECTOR_REGRESSION_INFO',
  },
  [RegressionModelEstimators.theilSen]: {
    label: 'ESTIMATOR_THEIL_SEN',
    value: RegressionModelEstimators.theilSen,
    isRecommended: false,
    description: 'ESTIMATOR_THEIL_SEN_INFO',
  },
  [RegressionModelEstimators.xgBoost]: {
    label: 'ESTIMATOR_X_G_BOOST',
    value: RegressionModelEstimators.xgBoost,
    isRecommended: false,
    description: 'ESTIMATOR_X_G_BOOST_INFO',
    isDisabled: true,
  },
  [RegressionModelEstimators.catBoost]: {
    label: 'ESTIMATOR_CAT_BOOST',
    value: RegressionModelEstimators.catBoost,
    isRecommended: false,
    description: 'ESTIMATOR_CAT_BOOST_INFO',
    isDisabled: true,
  },
};
